import { useAuditEvents, type AuditEventDto } from '@/audit/queries';
import { PageShell } from '@/components/page-shell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Pagination } from '@/components/ui/pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { useListQueryRoute } from '@/lib/use-list-query-route';

interface AuditFilter {
  types?: string | undefined;
  actorId?: string | undefined;
  targetType?: string | undefined;
}

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_CAP = 200;

const formatTimestamp = (iso: string): string => new Date(iso).toLocaleString('ru-RU');

const metadataPreview = (md: AuditEventDto['metadata']): string => {
  if (!md) return '';
  const keys = Object.keys(md).slice(0, 3);
  if (keys.length === 0) return '';
  return keys.map((k) => `${k}=${JSON.stringify(md[k])}`).join(' ');
};

export const AuditPage = () => {
  const lq = useListQueryRoute<AuditFilter, 'occurredAt'>({
    from: '/_protected/audit',
    defaultFilter: {
      types: undefined as string | undefined,
      actorId: undefined as string | undefined,
      targetType: undefined as string | undefined,
    },
    defaultSort: { field: 'occurredAt', dir: 'desc' },
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const pageSize = Math.min(Math.max(1, lq.pageSize), PAGE_SIZE_CAP);

  const query = useAuditEvents({
    page: lq.page,
    pageSize,
    ...(lq.filter.types ? { types: lq.filter.types } : {}),
    ...(lq.filter.actorId ? { actorId: lq.filter.actorId } : {}),
    ...(lq.filter.targetType ? { targetType: lq.filter.targetType } : {}),
  });

  const totalPages = query.data ? Math.max(0, Math.ceil(query.data.total / pageSize)) : 0;

  const filterSlot = (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span>Event types (CSV)</span>
        <Input
          className="w-64"
          value={lq.filter.types ?? ''}
          placeholder="campaign_launched,user_role_changed"
          onChange={(e) => {
            lq.updateQuery({ filter: { types: e.target.value || undefined } });
          }}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span>Actor (UUID)</span>
        <Input
          className="w-72"
          value={lq.filter.actorId ?? ''}
          placeholder="00000000-0000-0000-0000-000000000001"
          onChange={(e) => {
            lq.updateQuery({ filter: { actorId: e.target.value || undefined } });
          }}
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        <span>Target type</span>
        <Input
          className="w-44"
          value={lq.filter.targetType ?? ''}
          placeholder="campaign / account / user"
          onChange={(e) => {
            lq.updateQuery({ filter: { targetType: e.target.value || undefined } });
          }}
        />
      </label>
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          lq.resetQuery();
        }}
      >
        Сбросить
      </Button>
    </div>
  );

  return (
    <div className="space-y-4 p-6">
      <PageShell title="Аудит">
        <div className="space-y-3 p-6">
          {filterSlot}

          {query.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
            </div>
          ) : query.isError ? (
            <p className="text-sm text-destructive">Не удалось загрузить аудит-логи.</p>
          ) : query.data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Записей не найдено.</p>
          ) : (
            <ul className="divide-y rounded border">
              {query.data.items.map((event) => {
                const preview = metadataPreview(event.metadata);
                const hasJson =
                  event.metadata !== null || event.diffBefore !== null || event.diffAfter !== null;
                return (
                  <li key={event.id} className="px-3 py-2 text-sm">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <time
                        className="font-mono text-xs text-muted-foreground"
                        dateTime={event.occurredAt}
                        title={event.occurredAt}
                      >
                        {formatTimestamp(event.occurredAt)}
                      </time>
                      <Badge variant="outline">{event.eventType}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {event.userId ?? 'system'}
                      </span>
                      {event.targetType ? (
                        <span className="text-xs text-muted-foreground">
                          {event.targetType}/{event.targetId ?? '—'}
                        </span>
                      ) : null}
                      {preview ? (
                        <span className="truncate text-xs text-muted-foreground">{preview}</span>
                      ) : null}
                    </div>
                    {hasJson ? (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs text-muted-foreground">
                          metadata / diff
                        </summary>
                        <pre className="mt-1 overflow-x-auto rounded bg-muted p-2 text-xs">
                          {JSON.stringify(
                            {
                              metadata: event.metadata,
                              diffBefore: event.diffBefore,
                              diffAfter: event.diffAfter,
                            },
                            null,
                            2,
                          )}
                        </pre>
                      </details>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}

          <footer className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span>{query.data ? `Всего: ${String(query.data.total)}` : ''}</span>
            <Pagination
              page={lq.page}
              totalPages={totalPages}
              onPageChange={(page) => {
                lq.updateQuery({ page });
              }}
            />
          </footer>
        </div>
      </PageShell>
    </div>
  );
};
